"use client"

/*
  CallbackRequest.jsx
  - Uses ONLY the working endpoint: GET /client/{clientId}/recent-inquiries
  - Bootstrap 5 (via CDN included in app/layout.tsx)
  - Client-side search and pagination (page size dropdown, Prev/Next)
  - Details modal
  - Error + loading states
*/

import { useCallback, useEffect, useMemo, useRef, useState } from "react"

const BASE_URL = "https://ticketbot-api-uat.ataibot.in/public/api"

// Safe text helper
function text(v, fallback = "-") {
  return v === undefined || v === null || v === "" ? fallback : v
}

export default function CallbackRequest({ clientId: clientIdProp, pageSizeDefault = 10 }) {
  // Client ID resolution (prop or sessionStorage)
  const [clientId, setClientId] = useState("")
  useEffect(() => {
    const resolved =
      clientIdProp ||
      (typeof window !== "undefined" ? sessionStorage.getItem("client_id") || sessionStorage.getItem("clientId") : "")
    setClientId(resolved || "")
  }, [clientIdProp])

  // Data and UI state
  const [allInquiries, setAllInquiries] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(pageSizeDefault)

  const [selectedInquiry, setSelectedInquiry] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const lastFetchedFor = useRef(null)
  const hasFetchedRef = useRef(false)

  // Fetch ONLY the working endpoint: /client/{clientId}/recent-inquiries
  const fetchInquiries = useCallback(async (cid) => {
    if (!cid) return
    setLoading(true)
    setError("")

    try {
      const url = `${BASE_URL}/client/${cid}/recent-inquiries`
      const res = await fetch(url)
      if (!res.ok) throw new Error(`Failed to load inquiries (${res.status})`)
      const data = await res.json()

      // Expected shape from existing service: { client_id, new_inquiries_count, recent_inquiries }
      // Fallbacks included to be resilient.
      const rows =
        (Array.isArray(data?.recent_inquiries) && data.recent_inquiries) ||
        (Array.isArray(data?.data) && data.data) ||
        (Array.isArray(data?.items) && data.items) ||
        (Array.isArray(data) && data) ||
        []

      setAllInquiries(rows)
    } catch (e) {
      setError(e.message || "Failed to load inquiries.")
      setAllInquiries([])
    } finally {
      setLoading(false)
    }
  }, [])

  // useRef to guard against double fetch
  const fetchRef = useRef(false)

  // Load once when clientId available
  useEffect(() => {
    if (!clientId) return
    if (hasFetchedRef.current && lastFetchedFor.current === clientId) return
    hasFetchedRef.current = true
    lastFetchedFor.current = clientId
    fetchInquiries(clientId)
  }, [clientId, fetchInquiries])

  // Accessors (flexible to varying API fields)
  const getId = (r) => r?.ticket_id ?? r?.id ?? "-"
  const getName = (r) => r?.CustomerName ?? r?.Client_name ?? r?.name ?? "-"
  const getMobile = (r) => r?.MobileNumber ?? r?.contact ?? r?.mobile ?? "-"
  const getAssigned = (r) => r?.assignedName ?? r?.assignedTo ?? r?.staff_name ?? "-"
  const getDate = (r) => r?.InquiryDate ?? r?.createdAt ?? r?.created_at ?? r?.date ?? "-"
  const getStatus = (r) => r?.Status ?? r?.status ?? "-"
  const getDescription = (r) => r?.Description ?? r?.description ?? r?.other_query ?? ""

  // Client-side search + pagination
  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase().trim()
    if (!q) return allInquiries
    return allInquiries.filter((r) => {
      const id = String(getId(r) || "").toLowerCase()
      const nm = String(getName(r) || "").toLowerCase()
      const mb = String(getMobile(r) || "").toLowerCase()
      return id.includes(q) || nm.includes(q) || mb.includes(q)
    })
  }, [allInquiries, search])

  const total = filtered.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize
    return filtered.slice(start, start + pageSize)
  }, [filtered, page, pageSize])

  // Handlers
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value))
    setPage(1)
  }
  const handlePrev = () => setPage((p) => Math.max(1, p - 1))
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1))
  const openDetails = (row) => {
    setSelectedInquiry(row)
    setShowModal(true)
  }
  const closeDetails = () => {
    setSelectedInquiry(null)
    setShowModal(false)
  }

  return (
    <div className="container py-4">
      {/* Filters row: Search + Page Size (removed staff filter and staff-list API) */}
      <div className="d-flex flex-column flex-md-row align-items-md-end gap-3 mb-3">
        <div className="flex-grow-1">
          <label className="form-label">Search</label>
          <input
            type="text"
            className="form-control"
            placeholder="Search by Ticket ID, Name, or Mobile"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
          />
        </div>

        <div>
          <label className="form-label">Page Size</label>
          <select className="form-select" value={pageSize} onChange={handlePageSizeChange}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-3 text-primary">Recent Inquiries</h5>

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status" aria-label="Loading" />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-striped align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Ticket ID</th>
                    <th>Customer Name</th>
                    <th>Mobile Number</th>
                    <th>Assigned Staff</th>
                    <th>Inquiry Date</th>
                    <th>Status</th>
                    <th style={{ minWidth: 120 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.length > 0 ? (
                    pagedRows.map((row) => (
                      <tr key={String(getId(row))}>
                        <td>{text(getId(row))}</td>
                        <td className="text-break">{text(getName(row))}</td>
                        <td className="text-break">{text(getMobile(row))}</td>
                        <td className="text-break">{text(getAssigned(row))}</td>
                        <td className="text-break">{text(getDate(row))}</td>
                        <td>
                          <span
                            className={`badge ${
                              String(getStatus(row)).toLowerCase().includes("open")
                                ? "bg-warning text-dark"
                                : String(getStatus(row)).toLowerCase().includes("progress")
                                  ? "bg-info text-dark"
                                  : String(getStatus(row)).toLowerCase().includes("closed")
                                    ? "bg-success"
                                    : "bg-secondary"
                            }`}
                          >
                            {text(getStatus(row))}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-sm btn-primary" onClick={() => openDetails(row)}>
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        No inquiries found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <button className="btn btn-outline-primary btn-sm" onClick={handlePrev} disabled={page <= 1 || loading}>
              Prev
            </button>
            <div className="small">
              Page {page} of {totalPages}
            </div>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={handleNext}
              disabled={page >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Inquiry Details</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeDetails}></button>
                </div>
                <div className="modal-body">
                  {selectedInquiry ? (
                    <div className="row g-3">
                      <div className="col-md-6">
                        <strong>Ticket ID:</strong> {text(getId(selectedInquiry))}
                      </div>
                      <div className="col-md-6">
                        <strong>Status:</strong> {text(getStatus(selectedInquiry))}
                      </div>
                      <div className="col-md-6">
                        <strong>Customer Name:</strong> {text(getName(selectedInquiry))}
                      </div>
                      <div className="col-md-6">
                        <strong>Mobile:</strong> {text(getMobile(selectedInquiry))}
                      </div>
                      <div className="col-md-6">
                        <strong>Assigned Staff:</strong> {text(getAssigned(selectedInquiry))}
                      </div>
                      <div className="col-md-6">
                        <strong>Date:</strong> {text(getDate(selectedInquiry))}
                      </div>
                      <div className="col-12">
                        <strong>Description:</strong>
                        <div className="mt-1">{text(getDescription(selectedInquiry), "—")}</div>
                      </div>
                    </div>
                  ) : (
                    <div>Unable to load inquiry details.</div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeDetails}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Backdrop */}
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  )
}
